/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2018, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { Query, Mutation } from 'react-apollo'
import { withRouter } from 'react-router-dom'
import gql from 'graphql-tag'
import moment from 'moment'
import * as Sentry from '@sentry/browser'

import {
  Spinner,
  Warning,
  ProductCard,
  SpanWithData,
  TabbedView,
  CenteredContainerDisplay,
  TriggerableActionItem as ActionItem
} from '../'

import {
  TransactionHistory
} from './TransactionHistory'

import {
  HealthLevelIndicators
} from './HealthLevelIndicators'

import {
  upperCaseFirstCharacter,
  getNextBusinessDay
} from '../../utils'

import {
  ACCOUNT_TYPE_EARNBACKS,
  ACCOUNT_TYPE_EARNBACKS_MEMBERSHIP,
  IDENTITY_DION
} from '../../constants'

const TRANSACTION_HISTORY_TAB = 0

const QUERY_IDENTITY_EARNBACKS = gql`
  query ($identityId: ID!) {
    identity(id: $identityId) {
      id
      earnbacksStatus
      earnbacksType
      earnbacksSignupOn
      healthLevelCalc
      healthLevelLabel
      earnbacksBalance
      ebIsCancellable
      ebNotCancellableReason
    }
  }
`

const CANCEL_EARNBACKS = gql`
  mutation($input: CancelEarnBacksInput!) {
    cancelEarnbacks(input: $input) {
      identity {
        id
        earnbacksStatus
        earnbacksType
        earnbacksSignupOn
        healthLevelCalc
        healthLevelLabel
        earnbacksBalance
        ebIsCancellable
        ebNotCancellableReason
      }
    }
  }
`


class EarnbacksProductDetails extends React.PureComponent {
  static propTypes = {
    identityId: PropTypes.string.isRequired,
    className: PropTypes.string
  }

  static defaultProps = {
    className: ''
  }

  state = { openTab: TRANSACTION_HISTORY_TAB }

  changeOpenTabIndex = (tabIndex) => this.setState({ openTab: tabIndex })

  cancelEB = (cancelEarnbacks) => {
    cancelEarnbacks({
      variables: {
        input: {
          identityId: this.props.identityId
        }
      }
    })
    .catch(error => Sentry.captureMessage(`EB cancellation failed for identity ${this.props.identityId} - ${error}`))
  }

  render() {
    return (
      <Query
        query={QUERY_IDENTITY_EARNBACKS}
        variables={{ identityId: this.props.identityId }}>
        {({ loading, error, data: { identity } }) => {
          if (loading) {
            return <Spinner className='slds-is-relative' />
          }

          if (error) {
            return (
              <Warning className='u-font-size-larger' iconSize='medium'>
                Failed to load EarnBacks data.
              </Warning>
            )
          }

          return (
            <div className={`slds-size_1-of-1 stage-main ${this.props.className}`}>
              <div className='u-bg-color-white'>
                {/* overview section */}
                <div className='slds-p-around_large slds-border_bottom'>
                  <div className='slds-grid'>
                    <ProductCard
                      title='earnbacks'
                      subtitle={{ Status: <SpanWithData forField='IdentityEarnbacksStatus' searchKey={identity.earnbacksStatus} /> }}
                      product='earnbacks'
                      primaryDetail={{
                        label: 'Points balance',
                        value: identity.earnbacksBalance || 'Unknown'
                      }}
                      secondaryDetails={{
                        'Financial health level': identity.healthLevelLabel || '-',
                        'Earnbacks member since': identity.earnbacksSignupOn
                          ? moment(identity.earnbacksSignupOn).format("D MMM 'YY")
                          : 'Unknown',
                        'Membership type': upperCaseFirstCharacter(identity.earnbacksType)
                      }}
                      hideFooter={true}
                      inverseColours={true}
                      inverseFooterColours={true}
                      backgroundColour='darkslategray'
                      className='slds-col slds-text-color_inverse-weak product-card-earnbacks'
                    />
                    <CenteredContainerDisplay
                      content={
                        <div className='slds-size_3-of-4 slds-m-left_x-large'>
                          <div className='u-font-size-larger slds-m-bottom_medium'>
                            <b>Actions</b>
                          </div>
                          <Mutation mutation={CANCEL_EARNBACKS}>
                            {cancelEarnbacks => {
                              return (
                                <ActionItem
                                  name='Cancel EarnBacks membership'
                                  disabled={!identity.ebIsCancellable}
                                  disabledReason={identity.ebNotCancellableReason}
                                  triggerLabel='cancel'
                                  triggerOnClick={() => {this.cancelEB(cancelEarnbacks)}}
                                  className='slds-m-bottom_small'
                                />
                              )
                            }}
                          </Mutation>
                        </div>
                      }
                      yAdjustPercentage={-35}
                      useFullWidth={true}
                      className='slds-size_1-of-2'
                    />
                  </div>
                </div>
                {/* detail section */}
                <TabbedView
                  tabs={['Transaction history', 'Health level indicators', 'Membership transactions']}
                  contents={[
                    <TransactionHistory
                      identityId={this.props.identityId}
                      accountTypeId={ACCOUNT_TYPE_EARNBACKS}
                    />,
                    <HealthLevelIndicators healthLevelCalc={JSON.parse(identity.healthLevelCalc)} />,
                    <TransactionHistory
                      identityId={this.props.identityId}
                      accountTypeId={ACCOUNT_TYPE_EARNBACKS_MEMBERSHIP}
                    />,
                  ]}
                  tabSpacing='x-large'
                  selectedTab={this.state.openTab}
                  onClick={this.changeOpenTabIndex}
                  tabsClassName='slds-p-top_small slds-p-left_large slds-p-bottom_small slds-p-right_large'
                />
              </div>
            </div>
          )
        }}
      </Query>
    )
  }
}

export default withRouter(EarnbacksProductDetails)



// WEBPACK FOOTER //
// ./src/components/earnbacks-product-details/index.js